import { Group, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  SectionCard,
  StateBadge,
  useColumns,
} from "../../components/ui";
import { preOrderState } from "../../components/ui/statusBadge";
import { api, type PreOrderSummary } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";

/**
 * The Billing Executive's queue (REQ-SALES-1).
 *
 * Shows open day orders, which deliberately includes partially fulfilled ones:
 * a shortfall stays outstanding rather than the original request being
 * rewritten (Q1), so a day order can come back here more than once.
 */
export function PreOrderQueue() {
  const [preOrders, setPreOrders] = useState<PreOrderSummary[]>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function load() {
    try {
      setError(undefined);
      const { preOrders: loaded } = await api.preOrders("queue");
      setPreOrders(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the queue.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  /** Units still to supply, which is what confirming an order decides. */
  const outstandingOf = (preOrder: PreOrderSummary) =>
    preOrder.lineItems.reduce((n, line) => n + line.quantityOutstanding, 0);

  const columns = useColumns<PreOrderSummary>(
    () => [
      {
        key: "Day order",
        header: "Day order",
        width: "170px",
        render: (preOrder) => (
          <Text fz={13} fw={600} className="num" truncate>
            {preOrder.reference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (preOrder) => (
          <Text fz={14} truncate>
            {preOrder.customer.name}
          </Text>
        ),
      },
      {
        key: "Captured",
        header: "Captured",
        width: "120px",
        render: (preOrder) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(preOrder.createdAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
            })}
          </Text>
        ),
      },
      {
        key: "By",
        header: "By",
        width: "150px",
        render: (preOrder) => (
          <Text fz={13} c="gray.7" truncate>
            {preOrder.createdBy.name}
          </Text>
        ),
      },
      {
        key: "Lines",
        header: "Lines",
        width: "80px",
        align: "right",
        render: (preOrder) => (
          <Text fz={14} className="num">
            {preOrder.lineItems.length}
          </Text>
        ),
      },
      {
        key: "Outstanding",
        header: "Outstanding",
        width: "120px",
        align: "right",
        // Units still to supply. A partially fulfilled day order keeps its
        // shortfall rather than being rewritten (Q1), so this is the number
        // that decides whether it comes back to this queue again.
        render: (preOrder) => (
          <Text fz={14} fw={600} className="num">
            {outstandingOf(preOrder)}
          </Text>
        ),
      },
      {
        key: "Status",
        header: "Status",
        width: "160px",
        render: (preOrder) => (
          <StateBadge state={preOrderState(preOrder.status) ?? "preOrderPending"} />
        ),
      },
    ],
    [],
  );

  return (
    <Page
      title="Day Orders"
      subtitle="Waiting to be confirmed"
      back="/sales"
      width="wide"
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!preOrders && !error && <LoadingSkeleton variant="card" count={3} />}

      {preOrders?.length === 0 && (
        <EmptyState
          title="Nothing is waiting"
          description="Every day order has been fully supplied."
        />
      )}

      {preOrders && preOrders.length > 0 && (
        <ListView
          rows={preOrders}
          columns={columns}
          rowKey={(preOrder) => preOrder.id}
          onOpen={(preOrder) => navigate(`/sales/confirm/${preOrder.id}`)}
          exportDataset="pre-orders"
          caption="Day Orders"
          footnote="A day order reserves nothing. The price is re-resolved when the order is confirmed."
          renderCard={(preOrder) => (
        <SectionCard key={preOrder.id} title={preOrder.reference}>
          <Stack gap="md">
            <RecordCard
              to={`/sales/confirm/${preOrder.id}`}
              primary={preOrder.customer.name}
              secondary={`${new Date(preOrder.createdAt).toLocaleDateString()} · ${preOrder.type
                .replace(/_/g, " ")
                .toLowerCase()} · captured by ${preOrder.createdBy.name}`}
              badges={
                <StateBadge state={preOrderState(preOrder.status) ?? "preOrderPending"} />
              }
              meta={
                preOrder.status === "PARTIALLY_FULFILLED"
                  ? `Part supplied already — ${preOrder.totalOutstanding} still outstanding`
                  : undefined
              }
            />

            <Stack gap="xs">
              {preOrder.lineItems.map((line) => (
                <Group
                  key={line.productId}
                  justify="space-between"
                  align="flex-start"
                  wrap="nowrap"
                  gap="md"
                >
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text fz="md" c="gray.9">
                      {line.productName}
                    </Text>
                    <Text fz="sm" c="gray.6" className="num">
                      {line.sku}
                    </Text>
                  </Stack>
                  <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
                    <Text fz="md" c="gray.9" className="num">
                      <strong>{line.quantityOutstanding}</strong> of{" "}
                      {line.quantityRequested}
                    </Text>
                    <Text fz="sm" c="gray.6" className="num">
                      {formatINR(line.unitPriceAtCapture)} each at capture
                    </Text>
                  </Stack>
                </Group>
              ))}
            </Stack>

            {preOrder.notes && (
              <Text fz="sm" c="gray.6">
                Note: {preOrder.notes}
              </Text>
            )}
          </Stack>
        </SectionCard>
          )}
        />
      )}
    </Page>
  );
}
